import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Play, Pause, ChevronLeft, ChevronRight, CheckCircle2,
  User, Calendar, MapPin, Camera, Sparkles, ArrowRight,
  Rocket, Trophy, Image as ImageIcon, Building2, Instagram, Clock
} from 'lucide-react';

const SCENES = [
  {
    id: 1,
    title: 'Completa tu perfil',
    subtitle: 'Sube tu logo, cuenta qué hace tu negocio y agrega tus redes sociales.',
    accent: 'Perfil',
    icon: User,
    durationMs: 10000,
  },
  {
    id: 2,
    title: 'Crea tu primer evento',
    subtitle: 'Nombre, fecha, descripción y precio. En un par de minutos está listo.',
    accent: 'Evento',
    icon: Calendar,
    durationMs: 10000,
  },
  {
    id: 3,
    title: 'Elige la ubicación en el mapa',
    subtitle: 'Arrastra el pin a tu lugar exacto. Tu público te encuentra con un clic.',
    accent: 'Mapa',
    icon: MapPin,
    durationMs: 10000,
  },
  {
    id: 4,
    title: 'Sube hasta 3 fotos',
    subtitle: 'Una imagen vende más que mil palabras. Mostra tu vibe real.',
    accent: 'Fotos',
    icon: Camera,
    durationMs: 10000,
  },
  {
    id: 5,
    title: '¡Listo! Tu evento está en PlanCity',
    subtitle: 'Aparece en búsquedas, mapa y carrusel. Miles de personas pueden verlo.',
    accent: 'Publicado',
    icon: Sparkles,
    durationMs: 10000,
  },
];

// ================================================================
// Animated scene mockups (CSS-only). Each scene is a small UI story.
// ================================================================

const SceneProfile = () => (
  <div className="relative w-full h-full bg-gradient-to-br from-[#064E3B] via-[#032b21] to-[#021a14] overflow-hidden flex items-center justify-center p-8">
    <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-7 ob-scene-enter">
      <div className="flex items-center gap-4 mb-5">
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00CC66] to-[#064E3B] flex items-center justify-center text-white font-black text-2xl ob-avatar-pop">
            PC
          </div>
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#00CC66] rounded-full flex items-center justify-center ring-2 ring-white">
            <Camera className="w-3 h-3 text-white" strokeWidth={2.5} />
          </div>
        </div>
        <div className="flex-1">
          <div className="h-3 bg-gray-900 rounded-full w-2/3 mb-2 ob-line-fill" />
          <div className="h-2 bg-gray-200 rounded-full w-1/2 ob-line-fill" style={{ animationDelay: '0.3s' }} />
        </div>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-2 p-2.5 border border-gray-200 rounded-xl">
          <Building2 className="w-4 h-4 text-gray-400" />
          <div className="h-2 bg-gray-800 rounded-full ob-type-in" style={{ width: '65%', animationDelay: '0.6s' }} />
        </div>
        <div className="flex items-center gap-2 p-2.5 border border-gray-200 rounded-xl">
          <Instagram className="w-4 h-4 text-gray-400" />
          <div className="h-2 bg-gray-800 rounded-full ob-type-in" style={{ width: '50%', animationDelay: '1.2s' }} />
        </div>
        <div className="p-2.5 border border-gray-200 rounded-xl space-y-1.5">
          <div className="h-1.5 bg-gray-600 rounded-full w-full ob-type-in" style={{ animationDelay: '1.8s' }} />
          <div className="h-1.5 bg-gray-600 rounded-full w-4/5 ob-type-in" style={{ animationDelay: '2.1s' }} />
          <div className="h-1.5 bg-gray-600 rounded-full w-3/5 ob-type-in" style={{ animationDelay: '2.4s' }} />
        </div>
      </div>
      <div className="mt-5 ob-cta-pulse">
        <div className="bg-[#00CC66] text-white text-center py-2.5 rounded-full text-sm font-bold">
          Guardar perfil
        </div>
      </div>
    </div>
  </div>
);

const SceneEventForm = () => (
  <div className="relative w-full h-full bg-gradient-to-br from-[#021a14] via-[#032b21] to-[#064E3B] overflow-hidden flex items-center justify-center p-8">
    <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-7 ob-scene-enter">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#00CC66]" />
          <span className="font-bold text-[#064E3B]">Nuevo evento</span>
        </div>
        <Badge className="bg-[#00CC66]/10 text-[#00CC66] border-0">Borrador</Badge>
      </div>
      <div className="space-y-3">
        <div className="ob-field-drop" style={{ animationDelay: '0.1s' }}>
          <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Nombre</div>
          <div className="p-2.5 border border-[#00CC66] ring-2 ring-[#00CC66]/20 rounded-xl">
            <div className="h-2 bg-gray-800 rounded-full ob-type-in" style={{ width: '75%', animationDelay: '0.4s' }} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 ob-field-drop" style={{ animationDelay: '0.7s' }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Fecha</div>
            <div className="p-2.5 border border-gray-200 rounded-xl flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              <div className="h-2 bg-gray-600 rounded-full w-2/3" />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Hora</div>
            <div className="p-2.5 border border-gray-200 rounded-xl">
              <div className="h-2 bg-gray-600 rounded-full w-1/2" />
            </div>
          </div>
        </div>
        <div className="ob-field-drop" style={{ animationDelay: '1.3s' }}>
          <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Descripción</div>
          <div className="p-2.5 border border-gray-200 rounded-xl space-y-1.5">
            <div className="h-1.5 bg-gray-600 rounded-full w-full ob-type-in" style={{ animationDelay: '1.6s' }} />
            <div className="h-1.5 bg-gray-600 rounded-full w-11/12 ob-type-in" style={{ animationDelay: '1.9s' }} />
            <div className="h-1.5 bg-gray-600 rounded-full w-4/5 ob-type-in" style={{ animationDelay: '2.2s' }} />
          </div>
        </div>
        <div className="ob-field-drop" style={{ animationDelay: '2.5s' }}>
          <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Precio</div>
          <div className="p-2.5 border border-gray-200 rounded-xl flex items-center gap-2">
            <span className="text-[#00CC66] font-bold text-sm">$</span>
            <div className="h-2 bg-gray-800 rounded-full w-1/3 ob-type-in" style={{ animationDelay: '2.8s' }} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SceneMap = () => (
  <div className="relative w-full h-full overflow-hidden flex items-center justify-center p-8"
       style={{ background: 'linear-gradient(180deg, #1a3d2f 0%, #0d2a1e 100%)' }}>
    {/* Map grid */}
    <div className="absolute inset-0 opacity-30"
         style={{
           backgroundImage: 'linear-gradient(rgba(0,204,102,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(0,204,102,0.2) 1px, transparent 1px)',
           backgroundSize: '40px 40px'
         }} />
    {/* Roads */}
    <div className="absolute inset-0">
      <div className="absolute top-1/3 left-0 right-0 h-2 bg-[#00CC66]/20" />
      <div className="absolute top-2/3 left-0 right-0 h-1 bg-[#00CC66]/15" />
      <div className="absolute left-1/3 top-0 bottom-0 w-2 bg-[#00CC66]/20" />
      <div className="absolute left-2/3 top-0 bottom-0 w-1 bg-[#00CC66]/15" />
    </div>

    {/* Other pins scattered */}
    <div className="absolute top-[25%] left-[20%] opacity-40">
      <MapPin className="w-6 h-6 text-white" />
    </div>
    <div className="absolute top-[70%] left-[75%] opacity-40">
      <MapPin className="w-6 h-6 text-white" />
    </div>
    <div className="absolute top-[55%] left-[15%] opacity-40">
      <MapPin className="w-6 h-6 text-white" />
    </div>

    {/* Main pin - dropping */}
    <div className="relative z-10">
      <div className="ob-pin-drop">
        <div className="relative">
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-8 h-2 bg-black/40 rounded-full blur-sm ob-pin-shadow" />
          <div className="relative w-16 h-16 bg-[#00CC66] rounded-full flex items-center justify-center shadow-2xl ring-4 ring-white">
            <MapPin className="w-8 h-8 text-white" strokeWidth={2.5} fill="white" />
          </div>
          {/* Pulse rings */}
          <div className="absolute inset-0 rounded-full border-4 border-[#00CC66] ob-pulse-ring" />
          <div className="absolute inset-0 rounded-full border-4 border-[#00CC66] ob-pulse-ring" style={{ animationDelay: '0.8s' }} />
        </div>
      </div>
    </div>

    {/* Coord badge */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2 ob-coord-fade">
      <div className="w-2 h-2 rounded-full bg-[#00CC66] animate-pulse" />
      <span className="text-xs font-mono text-[#064E3B] font-bold">-23.6509, -70.3975</span>
    </div>
  </div>
);

const ScenePhotos = () => (
  <div className="relative w-full h-full bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a] overflow-hidden flex items-center justify-center p-8">
    <div className="w-full max-w-lg">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-xs text-white/70 uppercase tracking-widest font-bold">
          <Camera className="w-3.5 h-3.5" /> Galería del evento
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { grad: 'from-pink-500 via-orange-400 to-yellow-400', delay: '0.2s' },
          { grad: 'from-[#00CC66] via-teal-500 to-blue-500', delay: '0.9s' },
          { grad: 'from-purple-500 via-[#E91E8B] to-red-500', delay: '1.6s' },
        ].map((p, i) => (
          <div key={i} className="aspect-square rounded-2xl border-2 border-dashed border-white/20 overflow-hidden relative ob-photo-fill" style={{ animationDelay: p.delay }}>
            <div className={`absolute inset-0 bg-gradient-to-br ${p.grad}`} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-2 ob-photo-check" style={{ animationDelay: `calc(${p.delay} + 0.7s)` }}>
                <CheckCircle2 className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
            </div>
            <div className="absolute top-2 left-2 bg-black/40 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              {i + 1}/3
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-2 ob-upload-progress">
        <ImageIcon className="w-4 h-4 text-[#00CC66]" />
        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-[#00CC66] ob-progress-fill" />
        </div>
        <span className="text-white/70 text-xs font-mono">100%</span>
      </div>
    </div>
  </div>
);

const SceneLive = () => (
  <div className="relative w-full h-full bg-[#f8faf9] overflow-hidden flex items-center justify-center p-8">
    {/* Confetti dots */}
    <div className="absolute inset-0 pointer-events-none">
      {[...Array(12)].map((_, i) => (
        <div key={i} className="absolute w-2 h-2 rounded-full ob-confetti"
             style={{
               left: `${(i * 7) % 100}%`,
               top: `${(i * 11) % 100}%`,
               background: ['#00CC66', '#E91E8B', '#F7941D', '#29ABE2'][i % 4],
               animationDelay: `${i * 0.15}s`,
             }} />
      ))}
    </div>

    <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden ob-card-rise">
      {/* Hero image */}
      <div className="relative h-40 bg-gradient-to-br from-[#00CC66] via-teal-500 to-[#064E3B]">
        <Badge className="absolute top-3 left-3 bg-white text-[#00CC66] border-0 font-black">
          <Sparkles className="w-3 h-3 mr-1 fill-current" /> NUEVO
        </Badge>
        <Badge className="absolute top-3 right-3 bg-[#E91E8B] text-white border-0">
          Destacado
        </Badge>
      </div>
      {/* Card body */}
      <div className="p-5">
        <div className="font-black text-lg text-[#064E3B] mb-1">Tu evento publicado</div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
          <MapPin className="w-3 h-3" /> Antofagasta
          <span className="text-gray-300">•</span>
          <Calendar className="w-3 h-3" /> Próximamente
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <div className="w-8 h-8 rounded-full bg-[#00CC66] flex items-center justify-center ring-2 ring-white">
              <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
            <span className="font-semibold text-[#00CC66]">En línea</span>
          </div>
          <div className="text-xs text-gray-400 font-mono">plancity.cl</div>
        </div>
      </div>
    </div>

    {/* Big celebration ring */}
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="w-96 h-96 rounded-full border-4 border-[#00CC66]/20 ob-celebration-ring" />
    </div>
  </div>
);

const SCENE_COMPONENTS = [SceneProfile, SceneEventForm, SceneMap, ScenePhotos, SceneLive];

// ================================================================
// Main Onboarding Tab
// ================================================================

export const OnboardingTab = ({ provider, onGoToTab }) => {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);  // Don't auto-play — wait for user
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const accumulatedRef = useRef(0);

  const markAsSeenAndGoToEvents = () => {
    try {
      localStorage.setItem('plancity_onboarding_seen', '1');
    } catch {
      // localStorage blocked — non-fatal, just won't persist
    }
    if (typeof onGoToTab === 'function') {
      onGoToTab('events');
    }
  };

  const scene = SCENES[active];
  const SceneComp = SCENE_COMPONENTS[active];
  const duration = scene.durationMs;

  // Progress loop
  useEffect(() => {
    if (!playing) {
      cancelAnimationFrame(rafRef.current);
      return;
    }
    startRef.current = performance.now();
    const tick = (now) => {
      const elapsed = accumulatedRef.current + (now - startRef.current);
      const pct = Math.min(elapsed / duration, 1);
      setProgress(pct);
      if (pct >= 1) {
        accumulatedRef.current = 0;
        setProgress(0);
        setActive((i) => (i + 1) % SCENES.length);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, active, duration]);

  // Reset accumulated when scene changes
  useEffect(() => {
    accumulatedRef.current = 0;
    setProgress(0);
  }, [active]);

  const goTo = (idx) => {
    setActive(((idx % SCENES.length) + SCENES.length) % SCENES.length);
    accumulatedRef.current = 0;
    setProgress(0);
  };

  // Provider completion checklist
  const checklist = [
    { id: 'logo', label: 'Sube el logo de tu negocio', done: !!provider?.logo_url, tab: 'profile' },
    { id: 'description', label: 'Escribe una descripción de tu negocio', done: !!(provider?.description && provider.description.length > 10), tab: 'profile' },
    { id: 'phone', label: 'Agrega tu teléfono de contacto', done: !!provider?.phone, tab: 'profile' },
    { id: 'instagram', label: 'Conecta tu Instagram', done: !!provider?.instagram, tab: 'profile' },
    { id: 'event', label: 'Crea tu primer evento', done: (provider?.events_count || 0) > 0, tab: 'events' },
  ];
  const completed = checklist.filter(i => i.done).length;
  const pct = Math.round((completed / checklist.length) * 100);

  // Trial info
  const trialDays = provider?.trial_days_remaining;
  const hasTrial = !!provider?.trial_ends_at && typeof trialDays === 'number' && trialDays > 0;
  const trialPlan = (provider?.trial_plan || '').toUpperCase();
  // Color intensity: urgent if <=3 days, warn if <=7, regular otherwise
  const trialTone = trialDays <= 3 ? 'urgent' : trialDays <= 7 ? 'warn' : 'calm';

  return (
    <div className="space-y-6" data-testid="onboarding-tab">
      {/* ============ ONBOARDING STYLES ============ */}
      <style>{`
        @keyframes ob-scene-enter {
          0% { opacity: 0; transform: scale(0.96) translateY(10px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .ob-scene-enter { animation: ob-scene-enter 0.6s ease-out; }

        @keyframes ob-avatar-pop {
          0% { transform: scale(0); }
          60% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        .ob-avatar-pop { animation: ob-avatar-pop 0.7s ease-out 0.2s backwards; }

        @keyframes ob-line-fill {
          0% { transform: scaleX(0); transform-origin: left; }
          100% { transform: scaleX(1); transform-origin: left; }
        }
        .ob-line-fill { animation: ob-line-fill 0.5s ease-out 0.5s backwards; }

        @keyframes ob-type-in {
          0% { width: 0; opacity: 0.3; }
          100% { opacity: 1; }
        }
        .ob-type-in { animation: ob-type-in 0.6s ease-out backwards; }

        @keyframes ob-cta-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0,204,102,0.4); }
          50% { transform: scale(1.03); box-shadow: 0 0 0 10px rgba(0,204,102,0); }
        }
        .ob-cta-pulse { animation: ob-cta-pulse 2s ease-in-out 3s infinite; }

        @keyframes ob-field-drop {
          0% { opacity: 0; transform: translateY(-15px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .ob-field-drop { animation: ob-field-drop 0.5s ease-out backwards; }

        @keyframes ob-pin-drop {
          0% { transform: translateY(-300px) scale(0.5); opacity: 0; }
          60% { transform: translateY(20px) scale(1.1); opacity: 1; }
          75% { transform: translateY(-10px) scale(1); }
          90% { transform: translateY(5px); }
          100% { transform: translateY(0) scale(1); }
        }
        .ob-pin-drop { animation: ob-pin-drop 1.4s cubic-bezier(0.34, 1.56, 0.64, 1) backwards; }

        @keyframes ob-pin-shadow {
          0% { opacity: 0; transform: translateX(-50%) scaleX(0.3); }
          100% { opacity: 1; transform: translateX(-50%) scaleX(1); }
        }
        .ob-pin-shadow { animation: ob-pin-shadow 1.4s ease-out backwards; }

        @keyframes ob-pulse-ring {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        .ob-pulse-ring { animation: ob-pulse-ring 2s ease-out 1.4s infinite; }

        @keyframes ob-coord-fade {
          0% { opacity: 0; transform: translateX(-50%) translateY(20px); }
          100% { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        .ob-coord-fade { animation: ob-coord-fade 0.6s ease-out 2s backwards; }

        @keyframes ob-photo-fill {
          0% { opacity: 0; transform: scale(0.85) rotate(-4deg); }
          60% { opacity: 1; transform: scale(1.05) rotate(2deg); }
          100% { opacity: 1; transform: scale(1) rotate(0); }
        }
        .ob-photo-fill { animation: ob-photo-fill 0.7s ease-out backwards; }

        @keyframes ob-photo-check {
          0%, 50% { opacity: 0; transform: scale(0); }
          70% { opacity: 1; transform: scale(1.3); }
          100% { opacity: 1; transform: scale(1); }
        }
        .ob-photo-check { animation: ob-photo-check 0.5s ease-out backwards; }

        @keyframes ob-progress-fill {
          0% { width: 0; }
          100% { width: 100%; }
        }
        .ob-progress-fill { animation: ob-progress-fill 2.5s ease-out 1.5s forwards; width: 0; }

        @keyframes ob-upload-progress {
          0%, 30% { opacity: 0; }
          100% { opacity: 1; }
        }
        .ob-upload-progress { animation: ob-upload-progress 1s ease-out 1s backwards; }

        @keyframes ob-card-rise {
          0% { opacity: 0; transform: translateY(40px) scale(0.9); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .ob-card-rise { animation: ob-card-rise 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) backwards; }

        @keyframes ob-confetti {
          0% { opacity: 0; transform: translateY(0) scale(0); }
          20% { opacity: 1; transform: translateY(-50px) scale(1); }
          100% { opacity: 0; transform: translateY(-300px) scale(0.3) rotate(540deg); }
        }
        .ob-confetti { animation: ob-confetti 3s ease-out infinite; }

        @keyframes ob-celebration-ring {
          0% { opacity: 0; transform: scale(0.3); }
          40% { opacity: 1; }
          100% { opacity: 0; transform: scale(1.5); }
        }
        .ob-celebration-ring { animation: ob-celebration-ring 2.5s ease-out 0.5s infinite; }

        @keyframes ob-trial-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.4); }
          50% { box-shadow: 0 0 0 14px rgba(249, 115, 22, 0); }
        }
        .ob-trial-pulse { animation: ob-trial-pulse 2s ease-in-out infinite; }
      `}</style>

      {/* ============ TRIAL BANNER ============ */}
      {hasTrial && (
        <div
          className={`rounded-3xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 border-2 ${
            trialTone === 'urgent' ? 'bg-red-50 border-red-300' :
            trialTone === 'warn' ? 'bg-orange-50 border-orange-300' :
            'bg-gradient-to-r from-emerald-50 via-[#f0fdf4] to-emerald-50 border-[#00CC66]/30'
          }`}
          data-testid="trial-banner"
        >
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ob-trial-pulse ${
            trialTone === 'urgent' ? 'bg-red-500' :
            trialTone === 'warn' ? 'bg-orange-500' :
            'bg-[#00CC66]'
          }`}>
            <Clock className="w-7 h-7 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${
              trialTone === 'urgent' ? 'text-red-700' :
              trialTone === 'warn' ? 'text-orange-700' :
              'text-[#00CC66]'
            }`}>
              Trial gratis activo · Plan {trialPlan}
            </div>
            <h3 className={`font-black text-xl md:text-2xl leading-tight ${
              trialTone === 'urgent' ? 'text-red-900' :
              trialTone === 'warn' ? 'text-orange-900' :
              'text-[#064E3B]'
            }`}>
              {trialDays === 1 ? '¡Último día!' : `Te quedan ${trialDays} días gratis`}
            </h3>
            <p className={`text-sm mt-1 ${
              trialTone === 'urgent' ? 'text-red-700' :
              trialTone === 'warn' ? 'text-orange-700' :
              'text-gray-600'
            }`}>
              {trialTone === 'urgent'
                ? `Tu trial termina pronto. Habla con el administrador para mantener los beneficios de ${trialPlan}.`
                : trialTone === 'warn'
                  ? `Aprovecha los beneficios completos de ${trialPlan}. Contacta al administrador cuando quieras continuar.`
                  : `Disfruta todos los beneficios del plan ${trialPlan} sin costo. Luego vuelves a Basic si decides no continuar.`}
            </p>
          </div>
          <div className="flex-shrink-0 flex items-center gap-3">
            <div className="text-center px-4">
              <div className={`text-4xl md:text-5xl font-black leading-none ${
                trialTone === 'urgent' ? 'text-red-600' :
                trialTone === 'warn' ? 'text-orange-600' :
                'text-[#00CC66]'
              }`}>
                {trialDays}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-1">
                {trialDays === 1 ? 'día' : 'días'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ HERO ============ */}
      <Card className="border-0 shadow-sm rounded-3xl overflow-hidden">
        <div className="bg-gradient-to-br from-[#064E3B] via-[#032b21] to-[#021a14] p-8 md:p-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full"
               style={{ background: 'radial-gradient(circle, rgba(0,204,102,0.25) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <Badge className="bg-[#00CC66]/20 text-[#00CC66] border border-[#00CC66]/30 mb-3 uppercase tracking-widest text-[10px] font-bold px-3 py-1">
                <Rocket className="w-3 h-3 mr-1" /> Bienvenido a PlanCity
              </Badge>
              <h2 className="text-3xl md:text-4xl font-black text-white leading-tight mb-2">
                ¡Hola {provider?.business_name?.split(' ')[0] || 'proveedor'}! 👋
              </h2>
              <p className="text-white/70 text-base md:text-lg max-w-2xl">
                Te hicimos un tour rápido para que partas publicando hoy mismo.
                Mira el video y sigue los pasos al final.
              </p>
            </div>
            <div className="flex-shrink-0 bg-white/5 border border-white/10 rounded-2xl px-5 py-4 min-w-[200px]">
              <div className="text-[10px] uppercase tracking-widest text-[#00CC66] font-bold mb-1">Progreso inicial</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-black text-white">{pct}%</span>
                <span className="text-sm text-white/50">{completed}/{checklist.length} listos</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#00CC66] to-emerald-400 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* ============ VIDEO / ANIMATED TOUR ============ */}
      <Card className="border-0 shadow-sm rounded-3xl overflow-hidden" data-testid="onboarding-video">
        {/* Scene canvas */}
        <div className="relative aspect-[16/9] md:aspect-[2/1] bg-[#021a14]">
          <div key={active} className="absolute inset-0">
            <SceneComp />
          </div>

          {/* Top progress indicators */}
          <div className="absolute top-0 left-0 right-0 p-4 flex gap-1.5 z-20">
            {SCENES.map((_, i) => (
              <button key={i} onClick={() => goTo(i)}
                      className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden hover:bg-white/30 transition-colors"
                      data-testid={`ob-progress-${i}`}>
                <div className="h-full bg-white rounded-full"
                     style={{ width: i < active ? '100%' : i === active ? `${progress * 100}%` : '0%',
                              transition: i === active ? 'none' : 'width 0.2s' }} />
              </button>
            ))}
          </div>

          {/* Bottom overlay with title + controls */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent pt-16 pb-5 px-5 md:px-8 z-10">
            <div className="flex items-end justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-[#00CC66] flex items-center justify-center">
                    <scene.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-[#00CC66] font-bold">
                    Paso {active + 1} de {SCENES.length} · {scene.accent}
                  </span>
                </div>
                <h3 className="text-white text-xl md:text-2xl font-black leading-tight mb-1">
                  {scene.title}
                </h3>
                <p className="text-white/70 text-sm max-w-xl hidden md:block">
                  {scene.subtitle}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => goTo(active - 1)}
                        className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                        data-testid="ob-prev-btn" title="Anterior">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => setPlaying(p => !p)}
                        className="w-12 h-12 rounded-full bg-[#00CC66] hover:bg-[#00b359] text-white flex items-center justify-center transition-colors shadow-lg"
                        data-testid="ob-play-btn" title={playing ? 'Pausar' : 'Reproducir'}>
                  {playing ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current ml-0.5" />}
                </button>
                <button onClick={() => goTo(active + 1)}
                        className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                        data-testid="ob-next-btn" title="Siguiente">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <p className="text-white/70 text-sm mt-3 md:hidden">{scene.subtitle}</p>
          </div>
        </div>
      </Card>

      {/* ============ "YA VI EL VIDEO" — SKIP CTA ============ */}
      <div className="flex justify-center" data-testid="ob-skip-row">
        <button
          type="button"
          onClick={markAsSeenAndGoToEvents}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-gray-200 text-[#064E3B] font-semibold text-sm hover:bg-gray-50 hover:border-[#00CC66] hover:text-[#00CC66] transition-colors shadow-sm"
          data-testid="ob-skip-btn"
        >
          <CheckCircle2 className="w-4 h-4" />
          Ya vi el video — Ir a Mis Eventos
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* ============ CHECKLIST + QUICK ACTIONS ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Checklist */}
        <Card className="lg:col-span-2 border-0 shadow-sm rounded-3xl">
          <CardContent className="p-6 md:p-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[#00CC66]/10 text-[#00CC66] flex items-center justify-center">
                <Trophy className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-black text-[#064E3B] text-lg">Tu lista de arranque</h3>
                <p className="text-sm text-gray-500">Completa estos pasos para aprovechar al máximo PlanCity</p>
              </div>
            </div>
            <div className="space-y-2">
              {checklist.map((item) => (
                <div key={item.id}
                     className={`flex items-center gap-3 p-3.5 rounded-2xl border transition-colors ${
                       item.done ? 'bg-[#00CC66]/5 border-[#00CC66]/30' : 'bg-gray-50 border-gray-200'
                     }`}
                     data-testid={`onboarding-check-${item.id}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    item.done ? 'bg-[#00CC66] text-white' : 'bg-white border-2 border-gray-300'
                  }`}>
                    {item.done && <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} />}
                  </div>
                  <span className={`flex-1 text-sm font-medium ${item.done ? 'text-[#064E3B] line-through opacity-60' : 'text-gray-800'}`}>
                    {item.label}
                  </span>
                  {!item.done && (
                    <Button size="sm" variant="ghost"
                            className="text-[#00CC66] hover:bg-[#00CC66]/10 rounded-full h-8 px-3 text-xs font-bold"
                            onClick={() => onGoToTab?.(item.tab)}>
                      Ir <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick actions */}
        <Card className="border-0 shadow-sm rounded-3xl bg-gradient-to-br from-[#064E3B] to-[#032b21] text-white">
          <CardContent className="p-6 md:p-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[#00CC66] text-white flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <h3 className="font-black text-lg">Acciones rápidas</h3>
            </div>
            <div className="space-y-2.5">
              <button onClick={() => onGoToTab?.('events')}
                      className="w-full flex items-center gap-3 p-3.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-left transition-colors"
                      data-testid="ob-action-create-event">
                <div className="w-10 h-10 rounded-xl bg-[#00CC66] flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">Crear evento</div>
                  <div className="text-white/60 text-xs truncate">Publica en minutos</div>
                </div>
                <ArrowRight className="w-4 h-4 text-white/50 flex-shrink-0" />
              </button>

              <button onClick={() => onGoToTab?.('profile')}
                      className="w-full flex items-center gap-3 p-3.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-left transition-colors"
                      data-testid="ob-action-profile">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">Mi perfil</div>
                  <div className="text-white/60 text-xs truncate">Logo, descripción, redes</div>
                </div>
                <ArrowRight className="w-4 h-4 text-white/50 flex-shrink-0" />
              </button>

              <button onClick={() => onGoToTab?.('membership')}
                      className="w-full flex items-center gap-3 p-3.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-left transition-colors"
                      data-testid="ob-action-membership">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">Mi plan</div>
                  <div className="text-white/60 text-xs truncate">Crece cuando quieras</div>
                </div>
                <ArrowRight className="w-4 h-4 text-white/50 flex-shrink-0" />
              </button>
            </div>

            <div className="mt-5 pt-5 border-t border-white/10 text-xs text-white/50 leading-relaxed">
              💡 <strong className="text-white/80">Tip:</strong> Los proveedores que completan su perfil tienen hasta 3x más visitas.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingTab;
